<div class="profile-container">
  <div class="loading-overlay" *ngIf="isLoading">
    <div class="loading-spinner"></div>
    <div class="loading-content">
      <h3 class="loading-title">Loading Profile...</h3>
      <p class="loading-text">Fetching user information</p>
    </div>
  </div>

  <div class="error-overlay" *ngIf="hasError && !isLoading">
    <div class="error-content">
      <div class="error-icon">!</div>
      <h3 class="error-title">Unable to Load Profile</h3>
      <p class="error-text">{{ errorMessage }}</p>
      <button class="retry-button" (click)="loadUser()">Try Again</button>
    </div>
  </div>

  <div class="profile-content" *ngIf="!isLoading && !hasError">
    <div class="profile-header">
      <div class="user-info-card">
        <div class="profile-avatar">
          <img
            class="avatar-image"
            [src]="user?.profile_image || '../../assets/images/profile-pic.png'"
            alt="Profile picture"
          />
          <div class="avatar-badge" *ngIf="currentUser?.availableToPlay">
            <span class="status-dot"></span>
          </div>
        </div>
        <div class="user-details">
          <h2 class="username">{{ "@" + (user?.username || "") }}</h2>
          <p class="full-name">{{ user?.name }} {{ user?.last_name }}</p>
          <div class="user-meta">
            <span class="meta-item">
              <lucide-icon
                name="trophy"
                [size]="14"
                color="#f59e0b"
              ></lucide-icon>
              Rank #{{ user?.ranking || "N/A" }}
            </span>
            <span class="meta-item">
              <lucide-icon
                name="star"
                [size]="14"
                color="#f59e0b"
              ></lucide-icon>
              {{ user?.pp_points || 0 }} PP
            </span>
          </div>
        </div>
      </div>

      <div class="stats-grid">
        <div class="stat-card wins">
          <div class="stat-number">{{ user?.wins || 0 }}</div>
          <div class="stat-label">Wins</div>
        </div>
        <div class="stat-card losses">
          <div class="stat-number">{{ user?.loses || 0 }}</div>
          <div class="stat-label">Losses</div>
        </div>
        <div class="stat-card matches">
          <div class="stat-number">
            {{ (user?.wins || 0) + (user?.loses || 0) }}
          </div>
          <div class="stat-label">Total</div>
        </div>
      </div>
    </div>
    <div class="content-grid">
      <div class="info-section">
        <div class="section-card">
          <h3 class="section-title">Player Statistics</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">PP Ranking</span>
              <span class="info-value">#{{ user?.ranking || "N/A" }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">League</span>
              <span class="info-value">{{ getLeague() }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Total Points</span>
              <span class="info-value">{{ user?.pp_points || 0 }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Win Rate</span>
              <span class="info-value">{{ getWinRate() }}%</span>
            </div>
          </div>
        </div>
      </div>

      <div class="chart-section">
        <div class="section-card">
          <h3 class="section-title">RB Points Progression</h3>
          <div class="chart-container" *ngIf="!isLoadingGraph">
            <canvas
              baseChart
              [data]="lineChartData"
              [options]="lineChartOptions"
              [type]="lineChartType"
            ></canvas>
          </div>
          <div class="chart-loading" *ngIf="isLoadingGraph">
            <div class="loading-spinner"></div>
            <p class="loading-text">Loading progression data...</p>
          </div>
        </div>
      </div>
    </div>

    @if (id == currentUser?.id) {
    <div class="preferences-section">
      <div class="section-card">
        <h3 class="section-title">Settings & Preferences</h3>

        <div class="preferences-grid">
          <div class="availability-card">
            <div class="preference-item">
              <div class="preference-header">
                <span class="preference-label">Available to Play</span>
                <div class="availability-toggle" (click)="toggleAvailability()">
                  <div
                    class="toggle-slider"
                    [class.active]="currentUser?.availableToPlay"
                  >
                    <div class="toggle-handle"></div>
                  </div>
                  <span class="toggle-text">{{
                    currentUser?.availableToPlay ? "Available" : "Busy"
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="preferences-form">
            <div class="form-group">
              <label class="form-label">Preferred Playing Side</label>
              <div class="select-wrapper">
                <select
                  class="form-select"
                  [(ngModel)]="currentUser!.favouriteHand"
                  (change)="onSelectionChange()"
                >
                  <option value="right">Right Side</option>
                  <option value="left">Left Side</option>
                </select>
                <div class="select-icon">▼</div>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">Match Type Preference</label>
              <div class="select-wrapper">
                <select
                  class="form-select"
                  [(ngModel)]="currentUser!.favouriteMatch"
                  (change)="onSelectionChange()"
                >
                  <option value="competitive">Competitive</option>
                  <option value="friendly">Friendly</option>
                </select>
                <div class="select-icon">▼</div>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">Favorite Club</label>
              <div class="select-wrapper">
                <select
                  class="form-select"
                  [(ngModel)]="currentUser!.club_id"
                  (change)="onSelectionChange()"
                  [disabled]="isLoadingClubs"
                >
                  <option value="" disabled>
                    {{ isLoadingClubs ? "Loading clubs..." : "Select a club" }}
                  </option>
                  @for(club of clubs; track club.id) {
                  <option [value]="club.id">{{ club.name }}</option>
                  }
                </select>
                <div class="select-icon" *ngIf="!isLoadingClubs">▼</div>
                <div class="select-loading" *ngIf="isLoadingClubs">
                  <div class="mini-spinner"></div>
                </div>
              </div>
            </div>

            <div class="form-actions">
              <button
                class="save-button"
                [disabled]="!isDirty || isSaving"
                [class.loading]="isSaving"
                (click)="saveChanges()"
              >
                <div class="button-content">
                  <div class="button-spinner" *ngIf="isSaving"></div>
                  <span class="button-text">{{
                    isSaving ? "Saving..." : isDirty ? "Save Changes" : "Saved"
                  }}</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    }
  </div>
</div>
